import { airflow } from '@aviata/platform-client';
import type { MappedLocation, RouteState } from 'shared';

import type { RecentSearch } from '@/entities/recent-searches';

const getCode = (location: MappedLocation): string =>
  ('codeForBackend' in location && location.codeForBackend) || location.code;

type Itinerary = { dep: string; arr: string; date: string };

/**
 * One stored route → 1..N itineraries.
 * - 1 date  → one-way leg.
 * - 2 dates → round-trip pair (only valid for the simple form, where there is
 *   exactly one stored route).
 * Returns `null` when the route is missing required fields.
 */
const buildItinerariesFromRoute = (
  legRoute: RouteState,
): Itinerary[] | null => {
  if (!legRoute.dep || !legRoute.arr || !legRoute.dates?.[0]) {
    return null;
  }
  const departureCode = getCode(legRoute.dep);
  const arrivalCode = getCode(legRoute.arr);
  const [outboundDate, inboundDate] = legRoute.dates;
  const itineraries: Itinerary[] = [
    { dep: departureCode, arr: arrivalCode, date: outboundDate },
  ];
  if (inboundDate) {
    itineraries.push({
      dep: arrivalCode,
      arr: departureCode,
      date: inboundDate,
    });
  }
  return itineraries;
};

/**
 * Reconstructs a search hash code from a saved recent search, preserving the
 * mode it was saved in:
 *   - 1 route, 1 date  → one-way
 *   - 1 route, 2 dates → round-trip (outbound + inbound)
 *   - N routes, 1 date each → multi-trip (each leg becomes one itinerary)
 *
 * Returns `null` if any leg is missing dep/arr/date — the caller treats that
 * as "do not navigate".
 */
export const buildHashCodeFromRecentSearch = (
  search: RecentSearch,
): string | null => {
  if (!search.routes.length) {
    return null;
  }

  const itineraries: Itinerary[] = [];

  if (search.routes.length === 1) {
    const legItineraries = buildItinerariesFromRoute(search.routes[0]);
    if (!legItineraries) {
      return null;
    }
    itineraries.push(...legItineraries);
  } else {
    for (const legRoute of search.routes) {
      const legItineraries = buildItinerariesFromRoute(legRoute);
      if (!legItineraries) {
        return null;
      }
      // In multi-trip mode each leg is stored with a single departure date.
      // Defensive: ignore any extra dates rather than emit a stray return leg.
      itineraries.push(legItineraries[0]);
    }
  }

  const { adt, chd, inf, yth } = search.passengers;

  return airflow.utils.buildQuery({
    itineraries,
    passengers: search.isYouth
      ? { adt: 0, chd: 0, inf: 0, yth }
      : { adt, chd, inf, yth: 0 },
    cabinClass: search.cabinClass,
  });
};
